import { Navigate, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowUpRight, BookOpen, ChevronLeft, ClipboardList, FileText } from "lucide-react";
import { ListRow } from "../../components/ui/ListRow";
import { Skeleton } from "../../components/ui/Skeleton";
import { CourseChapterLists } from "../../components/study/CourseChapterLists";
import { findStudySubject } from "../../lib/studySubjects";
import { useStudySubjects } from "../../lib/useStudySubjects";
import { Badge } from "../../components/ui/Badge";
import { StudyModulesList, StudySubjectExams } from "./StudyHub";

type Tab = "notes" | "quiz" | "modules" | "exams";
const SEGMENTS: { id: "notes" | "quiz"; label: string }[] = [
  { id: "notes", label: "Notes" },
  { id: "quiz", label: "Quiz" },
];

/** Course page: Notes (default) and Quiz list the chapters in source order; module files and past exams sit below them. */
export function CoursePage() {
  const { subject = "" } = useParams<{ subject: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [search, setSearch] = useSearchParams();
  const { subjects, loading } = useStudySubjects();

  if (subject === "semester") return <Navigate to="/student/study" replace />;
  if (loading) return <Skeleton className="h-40 w-full" />;
  const course = findStudySubject(subjects, subject);
  if (!course) return <Navigate to="/student/study" replace />;

  const tabParam = search.get("tab");
  const tab: Tab = tabParam === "quiz" || tabParam === "modules" || tabParam === "exams" ? tabParam : "notes";
  const segment = tab === "quiz" ? "quiz" : "notes";
  const semester = (location.state as { fromSemester?: number } | null)?.fromSemester ?? course.semester ?? 1;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="space-y-3">
        <button
          type="button"
          onClick={() => (location.key !== "default" ? navigate(-1) : navigate(`/student/study/semester/${semester}`))}
          className="inline-flex min-h-11 items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft size={16} aria-hidden="true" /> Semester {semester}
        </button>
        <div className="flex items-center gap-3">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-accent-soft text-primary" aria-hidden="true">
            <BookOpen size={22} />
          </span>
          <div className="min-w-0">
            <h1 className="text-section font-bold font-display leading-tight">{course.name}</h1>
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <span className="font-mono">{course.code}</span>
              {(course as { isPremium?: boolean }).isPremium ? <Badge variant="warning">Premium</Badge> : <Badge variant="success">Free</Badge>}
            </p>
          </div>
        </div>
      </div>

      {tab === "modules" || tab === "exams" ? (
        <div className="space-y-4">
          <button type="button" onClick={() => setSearch({}, { replace: true })} className="inline-flex min-h-11 items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
            <ChevronLeft size={16} aria-hidden="true" /> Back to chapters
          </button>
          {tab === "modules" ? <StudyModulesList /> : <StudySubjectExams />}
        </div>
      ) : (
        <>
          <div role="tablist" aria-label="Course sections" className="flex gap-2">
            {SEGMENTS.map((t) => (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={segment === t.id}
                onClick={() => setSearch({ tab: t.id }, { replace: true })}
                className={`min-h-11 flex-1 rounded-btn border px-4 text-sm font-medium focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 sm:flex-none ${
                  segment === t.id ? "border-primary bg-primary text-on-accent" : "border-border bg-card"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          <CourseChapterLists courseId={course.id} slug={subject} tab={segment} semester={course.semester} />

          <div className="space-y-2">
            <h2 className="text-lg font-bold font-display">Also for this course</h2>
            <ListRow to="?tab=modules" icon={<FileText size={18} />} title="Module files" subtitle="The course files from your university" action={<ArrowUpRight size={18} className="text-muted-foreground" aria-hidden="true" />} />
            <ListRow to="?tab=exams" icon={<ClipboardList size={18} />} title="Past exams" subtitle="Practice with earlier exams" action={<ArrowUpRight size={18} className="text-muted-foreground" aria-hidden="true" />} />
          </div>
        </>
      )}
    </div>
  );
}
